import { Widget } from "../../enum";
import { IAcCfg, IBtn, IInput, IRead } from "../../@types/device";
import PairListEditor from "./PairListEditor";
import RangeEditor from "./RangeEditor";
import { TextField, NumberField } from "./Fields";

export type ParamState = {
  buttons: IBtn[];
  inputs: IInput[];
  reads: IRead[];
  slider: { min?: number; max?: number; step?: number };
  gauge: { min?: number; max?: number; unit?: string };
  rgb: { r: string; g: string; b: string };
  ac: Partial<IAcCfg>;
};

type Props = {
  widget: Widget | "";
  stateKey: string;
  state: ParamState;
  set: <K extends keyof ParamState>(k: K, v: ParamState[K]) => void;
  /** Live keys under the selected source, for the sub-editor datalists. */
  keys?: string[];
  addr?: string;
};

/** Counts the parameters in play, for the tab badge. */
export function paramCount(widget: Widget | "", s: ParamState): number {
  let n = s.inputs?.length ?? 0;
  if (widget === Widget.BUTTONS) n += s.buttons?.length ?? 0;
  if (widget === Widget.READOUT) n += s.reads?.length ?? 0;
  if (widget === Widget.SLIDER && s.slider?.max != null) n += 1;
  if (widget === Widget.GAUGE && s.gauge?.max != null) n += 1;
  if (widget === Widget.COLOR) n += 1;
  if (widget === Widget.AC) n += 1;
  return n;
}

export default function WidgetParams({
  widget,
  stateKey,
  state,
  set,
  keys = [],
  addr = "",
}: Props) {
  const list = addr ? `fb-keys-${addr}` : undefined;
  const ac = state.ac ?? {};
  const setAc = (patch: Partial<IAcCfg>) => set("ac", { ...ac, ...patch });

  return (
    <div className="space-y-6">
      {widget === Widget.BUTTONS && (
        <PairListEditor<IBtn>
          label="Buttons"
          value={state.buttons}
          onChange={(v) => set("buttons", v)}
          columns={[
            { key: "label", label: "Label", type: "text", className: "flex-1" },
            { key: "value", label: "Value", type: "number", className: "w-24" },
          ]}
          newRow={() => ({ label: "", value: 0 })}
          emptyHint="No buttons yet - the card will have no controls."
        />
      )}

      {widget === Widget.SLIDER && (
        <RangeEditor
          label="Slider range"
          value={state.slider}
          onChange={(v) => set("slider", v)}
          cells={[
            { key: "min", label: "Min" },
            { key: "max", label: "Max" },
            { key: "step", label: "Step" },
          ]}
        />
      )}

      {widget === Widget.GAUGE && (
        <RangeEditor
          label="Gauge range"
          value={state.gauge}
          onChange={(v) => set("gauge", v)}
          cells={[
            { key: "min", label: "Min" },
            { key: "max", label: "Max" },
            { key: "unit", label: "Unit", type: "text" },
          ]}
        />
      )}

      {widget === Widget.READOUT && (
        <PairListEditor<IRead>
          label="Readings"
          value={state.reads}
          onChange={(v) => set("reads", v)}
          columns={[
            { key: "key", label: "State key", type: "text", className: "flex-1", list },
            { key: "unit", label: "Unit", type: "text", className: "w-16" },
            { key: "scale", label: "Divide by", type: "number", className: "w-20" },
          ]}
          newRow={() => ({ key: stateKey, unit: "", scale: 1 })}
          emptyHint="No readings yet - the card will show nothing."
        />
      )}

      {widget === Widget.COLOR && (
        <div className="space-y-4">
          <div className="text-sm font-medium opacity-60">RGB state keys</div>
          <TextField
            label="Red"
            value={state.rgb?.r ?? ""}
            onChange={(v) => set("rgb", { ...state.rgb, r: v })}
            placeholder={`${stateKey}r`}
          />
          <TextField
            label="Green"
            value={state.rgb?.g ?? ""}
            onChange={(v) => set("rgb", { ...state.rgb, g: v })}
            placeholder={`${stateKey}g`}
          />
          <TextField
            label="Blue"
            value={state.rgb?.b ?? ""}
            onChange={(v) => set("rgb", { ...state.rgb, b: v })}
            placeholder={`${stateKey}b`}
          />
        </div>
      )}

      {widget === Widget.AC && (
        <div className="space-y-4">
          <div className="text-sm font-medium opacity-60">Sensors</div>
          <TextField
            label="Indoor key"
            value={ac.indoor?.key ?? ""}
            onChange={(v) => setAc({ indoor: { key: v, addr: "data", scale: 10 } })}
            hint="Read from /data, divided by 10."
          />
          <TextField
            label="Outdoor key"
            value={ac.outdoor?.key ?? ""}
            onChange={(v) => setAc({ outdoor: { key: v, addr: "data", scale: 10 } })}
            hint="Read from /data, divided by 10."
          />
          <div className="text-sm font-medium opacity-60">Controls</div>
          <TextField
            label="Target key"
            value={ac.targetKey ?? ""}
            onChange={(v) => setAc({ targetKey: v })}
          />
          <NumberField
            label="Target min"
            value={ac.targetMin}
            onChange={(v) => setAc({ targetMin: v })}
            placeholder="19"
          />
          <NumberField
            label="Target max"
            value={ac.targetMax}
            onChange={(v) => setAc({ targetMax: v })}
            placeholder="30"
          />
          <TextField
            label="Fan key"
            value={ac.fanKey ?? ""}
            onChange={(v) => setAc({ fanKey: v })}
          />
          <NumberField
            label="Fan max"
            value={ac.fanMax}
            onChange={(v) => setAc({ fanMax: v })}
            placeholder="5"
          />
          <TextField
            label="Swing key"
            value={ac.swingKey ?? ""}
            onChange={(v) => setAc({ swingKey: v })}
          />
          <TextField
            label="Running key"
            value={ac.runningKey ?? ""}
            onChange={(v) =>
              setAc({ runningKey: v || undefined, runningAddr: v ? "data" : undefined })
            }
            hint="Optional. Read from /data."
          />
        </div>
      )}

      {/* Always shown. This is what makes pumps (buttons + inputs) and the
          settings card (no body, inputs only) need no special case. */}
      <PairListEditor<IInput>
        label="Extra inputs"
        value={state.inputs}
        onChange={(v) => set("inputs", v)}
        columns={[
          { key: "label", label: "Label", type: "text", className: "flex-1" },
          { key: "key", label: "State key", type: "text", className: "flex-1", list },
        ]}
        newRow={() => ({ label: "", key: "" })}
        emptyHint="None. Add one for things like a pump interval."
      />
    </div>
  );
}
